/**
 * The research conversation model.
 *
 * A turn is a discriminated union, NOT the legacy `Message` string-bag:
 *
 *   { kind: 'user',      text, ... }
 *   { kind: 'assistant', response?: ResearchResponse, live?: ..., ... }
 *
 * An assistant turn's renderer is chosen from `response.intent` - the typed
 * envelope the adapter produced - never by sniffing JSON keys off a text
 * field. A turn that is still streaming has no `response` yet; it has `live`
 * activity instead, and the two states are distinguishable by type rather
 * than by a boolean soup of isTyping / isFlowDone / isPreFormatted.
 */

import {
  ResearchIntent,
  ResearchResponse,
  VerificationReport,
  isResearchIntent,
} from './types';
import { ensureUTCString } from '../../utils/functions';
import { ResearchMessageItem } from '../../store/actions/ChatAction';
import { ActivityStage } from './activity';
import { TurnAttachment, attachmentsFromMessage } from './attachments';
import { VersionInfo, defaultVersion, versionsFromMessage } from './versions';
import { TurnClarification } from './clarify';

/** Blob-hydration state for a history turn. Live turns are born hydrated. */
export type HydrationState = 'pending' | 'loading' | 'hydrated' | 'failed';

export interface UserTurn {
  kind: 'user';
  id: string;
  /** Backend message uuid, absent only for an optimistic local echo. */
  messageUuid?: string;
  text: string;
  createdAt: Date;
  /** Keyset cursor for paging further back. */
  cursor?: string;
  /**
   * Files sent with this message.
   *
   * Uploaded through their own endpoint, so this is only populated when the
   * message row echoes them back (history) or when the send carried them
   * (the live turn). Empty means "none to show", never an error.
   */
  attachments?: TurnAttachment[];
}

export interface AssistantTurn {
  kind: 'assistant';
  id: string;
  messageUuid?: string;
  createdAt: Date;
  cursor?: string;

  /** The typed answer. Absent while streaming, or before hydration. */
  response?: ResearchResponse;

  /** SQL summary - what a not-yet-hydrated history turn renders. */
  summary?: string;
  /** Known before the blob loads, so the right skeleton shows. */
  intent?: ResearchIntent | null;
  citationCount?: number;
  hydration?: HydrationState;

  /** Live pipeline activity while the turn is in flight. */
  /** Conversational lines emitted during the run, in order. */
  interim?: string[];
  /**
   * The engine's tailored progress phrases for this question, for the
   * working indicator. Absent until it sends them - and on a fast path it
   * never does, which is exactly why the indicator has a fallback.
   */
  cues?: string[];
  live?: {
    stages: ActivityStage[];
    done: boolean;
    error: string | null;
  };

  /**
   * THIS SESSION opened a run for this turn - the user is watching its answer
   * arrive, rather than reading one back from history.
   *
   * Deliberately separate from `live`, which is a CHANNEL and not a history.
   * Every path that settles a turn clears `live` on purpose - the blob-fetch
   * reconcile, lazy hydration, the clarify hand-off - because anything left
   * on it reads as "still running". So `live` answers "is a run open now?",
   * which is false by the time the answer is on screen, and it cannot answer
   * "did this answer just arrive?".
   *
   * Set wherever a run is OPENED and never cleared, so it survives the
   * settle. A turn built from SQL never carries it.
   */
  liveArrival?: boolean;

  /** R5 groundedness annotation, surfaced with the answer. */
  verification?: VerificationReport;

  /**
   * The turn's answer history - one entry per run, v1 first.
   *
   * Always present from the backend, with a single synthetic entry for a turn
   * that has never been re-run, so the toggle keys off `versionCount > 1`.
   */
  versions?: VersionInfo;
  /** Which version is on screen. Defaults to the current one. */
  activeVersion?: number;
  /** A version switch is fetching. */
  versionLoading?: boolean;
  /** Why a re-run was refused, in the turn's own words. */
  rerunError?: string;

  /** This user's rating of the answer: up, down, or none. */
  feedback?: 'up' | 'down' | null;

  /**
   * An outstanding clarifying question, in place of an answer.
   *
   * DELIBERATELY A FIELD ON THE ASSISTANT TURN rather than a third turn kind.
   * The pending question IS this turn - same message uuid, same sequence
   * position, and answering it resumes it rather than starting another. A
   * separate kind would have said the opposite, and would have made every
   * `kind === 'user' ? … : …` in the surface wrong.
   *
   * It also means the transcript renders the question exactly where the
   * conversation left off, which is the whole point of persisting it.
   */
  clarification?: TurnClarification;

  /**
   * The message page said this turn is an unanswered question.
   *
   * Known from SQL alone (`pending` on the row), one page read, no N+1 - so
   * the transcript can paint the question's PLACE before `GET /pending` has
   * returned its text. `clarification` fills in a moment later.
   */
  awaitingClarification?: boolean;
}

export type ResearchTurn = UserTurn | AssistantTurn;

export const isAssistantTurn = (turn: ResearchTurn): turn is AssistantTurn =>
  turn.kind === 'assistant';

/** A turn is still streaming when it has live activity that has not finished. */
export function isStreaming(turn: ResearchTurn): boolean {
  return isAssistantTurn(turn) && !!turn.live && !turn.live.done;
}

/* ------------------------------------------------------------------ */
/*  What an assistant turn should render                               */
/* ------------------------------------------------------------------ */

/**
 * The turn's display state - mutually exclusive, decided in ONE place.
 *
 *   streaming   the run is in flight        → progress, never an answer
 *   answered    completed WITH content      → the typed renderer
 *   empty       completed with NO content   → "nothing found", and ONLY here
 *   failed      the run errored             → the error
 *   unhydrated  a history turn, blob pending→ summary + shape skeleton
 *
 * Ordering matters: `streaming` is checked FIRST and wins over everything.
 * An in-flight turn must never fall through to an answer branch - that is
 * how a "no documents found" empty state came to be shown for the whole
 * pipeline and then swapped for the real answer at the end.
 */
export type TurnDisplayState =
  | 'streaming'
  | 'answered'
  | 'empty'
  | 'failed'
  | 'unhydrated';

/** Does this envelope actually carry something to show? */
export function responseHasContent(response: ResearchResponse | undefined): boolean {
  if (!response) return false;
  if (response.meta.payloadValid) return true;
  if ((response.markdown || '').trim() !== '') return true;
  return response.citations.length > 0;
}

export function turnDisplayState(turn: AssistantTurn): TurnDisplayState {
  // An answer with content ends the run, whatever the live flag says. The
  // flag can be left false by a dropped final event or by the mirror
  // detaching first, and a turn that has its answer must never keep spinning.
  if (turn.response && responseHasContent(turn.response)) return 'answered';
  // In flight - nothing about the answer is known yet, whatever else is set.
  if (turn.live && !turn.live.done) return 'streaming';
  if (turn.live?.error) return 'failed';
  if (turn.response) {
    if (responseHasContent(turn.response)) return 'answered';
    /*
      EMPTY IS A CLAIM ABOUT A FINISHED RUN, and it has to be earned.

      A contentless envelope can sit on a turn that has not produced its
      result yet - most visibly on the beat after a clarify is answered, when
      the turn is resuming and the run object has been swapped out from under
      it. Reading that as "no relevant documents were found" told the user the
      search had failed, a moment before the answer streamed in and replaced
      it. Nothing had failed; nothing had happened yet.

      So an empty envelope only means "nothing found" once something says the
      run is over: `live.done`, or a turn read back from history whose blob has
      already been fetched. Otherwise it is a not-yet, and a not-yet is the
      processing state.
    */
    const finished =
      turn.live?.done === true ||
      turn.hydration === 'hydrated' ||
      turn.hydration === 'failed';
    return finished ? 'empty' : 'streaming';
  }
  // A finished run with no envelope at all is a failure, not an empty answer.
  if (turn.live?.done) return 'failed';
  if (turn.summary) return 'unhydrated';
  return turn.hydration === 'failed' ? 'failed' : 'unhydrated';
}

/**
 * Why this turn has no answer, in one place.
 *
 * A run can fail in three ways and they used to read as three different
 * things on screen: the pipeline erroring mid-run (`live.error`), a re-run
 * being refused before it started (`rerunError`), and a version's blob
 * failing to load. To the user they are one situation - "this didn't work,
 * let me try again" - so they resolve to one reason string and one rendered
 * state.
 */
export function failureReason(turn: AssistantTurn): string {
  return (
    String(turn.live?.error || '').trim() ||
    String(turn.rerunError || '').trim() ||
    (turn.hydration === 'failed' ? 'This answer could not be loaded.' : '')
  );
}

/**
 * Should this turn render as failed?
 *
 * Broader than `turnDisplayState`, deliberately: a turn can hold a perfectly
 * good answer AND a refusal to re-run it (a 429, say), and the refusal still
 * has to be visible. The display state governs the BODY; this governs whether
 * the failure block appears with it.
 */
export function hasFailure(turn: AssistantTurn): boolean {
  return failureReason(turn) !== '';
}

/* ------------------------------------------------------------------ */
/*  History                                                            */
/* ------------------------------------------------------------------ */

export const HISTORY_PAGE_SIZE = 20;

/**
 * Message statuses that mean "the engine is still working on this".
 *
 * A hard refresh mid-run reloads the transcript from SQL, where the row is
 * still open. Without reading the status the turn came back as a finished
 * one with no content - which `turnDisplayState` correctly called `empty`,
 * and the user saw "No relevant documents were found" for a question that was
 * still being answered.
 */
const RUNNING_STATUSES = new Set([
  'processing', 'pending', 'in_progress', 'running', 'queued', 'started',
  // The clarify gate's brief window between "the answers resolved it" and the
  // queue message landing. Without it, reopening a chat in that half-second
  // renders a turn that is about to run as one that finished with nothing.
  'planned',
]);

export function isRunningStatus(status: unknown): boolean {
  return RUNNING_STATUSES.has(String(status || '').trim().toLowerCase());
}

/**
 * A turn the engine is still working on, that this client is not watching.
 *
 * A turn holding an unanswered QUESTION is explicitly not one of these, even
 * if a stale `live` is still hanging off it. Nothing is running: the engine
 * held before retrieval and is waiting on the user, so the content blob does
 * not exist and will not until they answer. Sweeping it polls
 * `/messages/<id>/content` every fifteen seconds forever, gets a NOT_FOUND
 * body back every time, and that endless spin is what reads as "it failed".
 */
export function isResumable(turn: ResearchTurn): boolean {
  return (
    isAssistantTurn(turn) &&
    !!turn.messageUuid &&
    !turn.response &&
    !turn.awaitingClarification &&
    !isAwaitingClarification(turn) &&
    !!turn.live &&
    !turn.live.done
  );
}

/**
 * Message status meaning "this turn is a question the user has not answered".
 *
 * Durable, and NOT running: nothing was enqueued and nothing is being spent.
 * It is checked before the running statuses everywhere it matters, because a
 * turn that is waiting on the USER must never render as a turn that is waiting
 * on the ENGINE - a spinner on a question is a lie the user cannot act on.
 */
export const AWAITING_CLARIFICATION_STATUS = 'awaiting_clarification';

export function isAwaitingStatus(status: unknown): boolean {
  return String(status || '').trim().toLowerCase() === AWAITING_CLARIFICATION_STATUS;
}

/** A turn holding a question that is still open. */
export function isAwaitingClarification(turn: ResearchTurn): boolean {
  return (
    isAssistantTurn(turn) &&
    !!turn.clarification &&
    !turn.clarification.settled
  );
}

/** The turn a pending question belongs to, if it is on screen. */
export function turnForMessage(
  turns: ResearchTurn[],
  messageUuid: string
): AssistantTurn | undefined {
  return turns.find(
    (turn): turn is AssistantTurn =>
      isAssistantTurn(turn) && turn.messageUuid === messageUuid
  );
}

/** Assistant turns whose blob has not been fetched yet. */
export function needsHydration(turn: ResearchTurn): boolean {
  return (
    isAssistantTurn(turn) &&
    !!turn.messageUuid &&
    // A question has no blob and never will until it is answered. Fetching one
    // produces an empty envelope and retires the turn as answered-with-nothing
    // - the same failure the running-turn guard below exists to prevent.
    !turn.awaitingClarification &&
    !isAwaitingClarification(turn) &&
    // A running turn has no blob yet; fetching one would only produce an
    // empty envelope and retire the turn as answered-with-nothing.
    !isResumable(turn) &&
    turn.hydration !== 'hydrated' &&
    turn.hydration !== 'loading'
  );
}

/** Sort key - `sequence_number` when present, else the timestamp. */
function orderKey(item: ResearchMessageItem): number {
  if (typeof item.sequence_number === 'number') return item.sequence_number;
  const ms = Date.parse(item.created_at);
  return Number.isFinite(ms) ? ms : 0;
}

/**
 * One SQL row → one renderable turn, with ZERO extra requests.
 *
 * The row carries `summary` and `intent`, which is enough to paint the whole
 * conversation on open; the blob is fetched lazily, per turn, on view.
 */
/**
 * The message uuid a stored blob claims for itself, if it declares one.
 *
 * Used to refuse a blob that belongs to a different turn - one turn's answer
 * rendered under another's question is worse than no answer at all.
 */
export function blobMessageUuid(blob: any): string {
  const raw =
    blob?.message_uuid ??
    blob?.messageUuid ??
    blob?.synthesis?.message_uuid ??
    '';
  return typeof raw === 'string' ? raw.trim() : '';
}

export function toTurn(item: ResearchMessageItem): ResearchTurn {
  // UTC-normalised: a naive timestamp parsed as local shifts every turn by
  // the reader's offset.
  const createdAt = new Date(ensureUTCString(item.created_at || ''));

  if (item.role === 'user') {
    const attachments = attachmentsFromMessage(item);
    return {
      kind: 'user',
      id: `${item.uuid}_user`,
      messageUuid: item.uuid,
      text: item.summary || item.content || '',
      createdAt,
      cursor: item.cursor,
      ...(attachments.length ? { attachments } : {}),
    };
  }

  const versions = versionsFromMessage(item);
  return {
    kind: 'assistant',
    id: `${item.uuid}_assistant`,
    messageUuid: item.uuid,
    createdAt,
    cursor: item.cursor,
    summary: item.summary || '',
    intent: isResearchIntent(item.intent) ? item.intent : null,
    citationCount: item.citation_count,
    hydration: 'pending',
    versions,
    activeVersion: defaultVersion(versions),
    feedback: item.feedback === 'up' || item.feedback === 'down' ? item.feedback : null,
    // An OPEN QUESTION. Checked before the running statuses because a turn
    // waiting on the user is not a turn waiting on the engine: it gets no
    // spinner, no resume sweep and no blob fetch.
    ...(item.pending || isAwaitingStatus(item.status)
      ? { awaitingClarification: true }
      // Still running server-side: reopen it as in-flight rather than letting
      // it render as a finished answer with nothing in it.
      : isRunningStatus(item.status)
      ? { live: { stages: [], done: false, error: null } }
      : {}),
  };
}

/** A page of rows → turns in reading order (oldest first). */
export function toTurns(items: ResearchMessageItem[]): ResearchTurn[] {
  return [...(items || [])].sort((a, b) => orderKey(a) - orderKey(b)).map(toTurn);
}

/**
 * The transcript's render order: original chronological order, always.
 *
 * A conversation is a sequence, so the order it happened in is the only
 * correct one. Version grouping and artifact collection are VIEWS over the
 * turns and must never reorder them - a timeline that groups with an earlier
 * draft still belongs where it was asked.
 *
 * The sort is stable on equal timestamps (a user turn and its assistant turn
 * are created in the same millisecond), so insertion order breaks ties and a
 * question can never render after its answer.
 */
export function orderTurns(turns: ResearchTurn[]): ResearchTurn[] {
  return (turns || [])
    .map((turn, index) => ({ turn, index }))
    .sort((a, b) => {
      const at = a.turn.createdAt?.getTime?.() ?? 0;
      const bt = b.turn.createdAt?.getTime?.() ?? 0;
      if (at !== bt) return at - bt;
      return a.index - b.index;
    })
    .map((entry) => entry.turn);
}

/** Prepend an older page, dropping anything already on screen. */
export function prependOlder(older: ResearchTurn[], current: ResearchTurn[]): ResearchTurn[] {
  const seen = new Set(current.map((t) => t.id));
  return [...older.filter((t) => !seen.has(t.id)), ...current];
}

/** The newest assistant turn - hydrated eagerly, it is what the user sees. */
export function newestAssistantUuid(turns: ResearchTurn[]): string | undefined {
  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const turn = turns[i];
    if (isAssistantTurn(turn) && turn.messageUuid) return turn.messageUuid;
  }
  return undefined;
}

/** Replace one turn by id, leaving the rest untouched. */
export function patchTurn(
  turns: ResearchTurn[],
  id: string,
  patch: (turn: AssistantTurn) => AssistantTurn
): ResearchTurn[] {
  return turns.map((turn) =>
    turn.id === id && isAssistantTurn(turn) ? patch(turn) : turn
  );
}
